import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetState } from "react-use";
import { client } from "../../bit/board/hooks/useGraphQLClient";
import PinInputs from "../../components/PinInputs";
import VirtualKeyboard, {
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import { isPWA, isVirtualKeyboardEnabled } from "../../config/const";
import { QUERY_DIGITAL_RECEPTION_PUBLIC } from "../../config/graphql/query";
import { useOfflineContext } from "../../context/Offline";

const defaultPinCode = {
  1: "",
  2: "",
  3: "",
  4: "",
  5: "",
  6: "",
};

const Activation = () => {
  const { t } = useTranslation(["check"]);
  const { storeData, getData, refreshUntilMark } = useOfflineContext();

  const [focusedInput, setFocusedInput] = useState<undefined | string>("1");
  // inputted by user
  const [pinCode, setPinCode] = useSetState(defaultPinCode);
  const [code, setCode] = useState<string | undefined>("");

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const onKeyboardChange = useCallback(
    (value: string) => {
      const regex = /^\d$/;

      if (
        focusedInput &&
        +focusedInput < 7 &&
        (value === "" || regex.test(value))
      ) {
        /**
         * Reset virtual input after we set the code
         */
        if (keyboard.current) {
          keyboard.current.setInput("", focusedInput);
        }

        setPinCode(() => ({ [focusedInput]: value }));

        if (focusedInput === "6" && value.length) {
          setFocusedInput(undefined);
        } else {
          if (document.activeElement) {
            (document.activeElement as HTMLInputElement)?.blur();
          }

          setFocusedInput(`${+focusedInput + 1}`);
        }
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button: string) => {
      if (button === "{bksp}" && typeof focusedInput !== "undefined") {
        setPinCode(() => ({ [focusedInput]: "" }));

        const nextFocusedInput = Math.max(1, +focusedInput - 1);

        setFocusedInput(`${nextFocusedInput}`);
      }
    },
    [focusedInput],
  );

  // const code = Object.values(pinCode).join("");

  const getCachedCodeAndPlayerUrl = useCallback(
    async () => {
      let code: string | undefined;
      let playerUrl:  string | undefined;

      try {
        code = await getData<string>("/activation-code");
      } catch {
        // do nothing
      }

      try {
        playerUrl = await getData<string>("/player-url");
      } catch {
        // do nothing
      }

      return { code, playerUrl };
    },
    [],
  );

  useEffect(() => {
    const getCode = async () => {
      const enteredCode = Object.values(pinCode).join("");

      if (!enteredCode) {
        // get code from cache if the user has not entered any code
        const { code, playerUrl } = (await getCachedCodeAndPlayerUrl()) || {};

        if (playerUrl) {

          const hasTrailingSlash = playerUrl.endsWith("/");
          window.location.replace(playerUrl + (hasTrailingSlash ? "" : "/"));
          return;
        }

        setCode(() => code);
        return;
      }

      setCode(() => Object.values(pinCode).join(""));
    };

    getCode();
  }, [pinCode]);

  useQuery(
    ["activation", code],
    async () => {
      try {
        const { digitalReceptionPublic } = await client.request<{
          digitalReceptionPublic: { playerUrl: string };
        }>(QUERY_DIGITAL_RECEPTION_PUBLIC, { input: { code } });

        storeData("/activation-code", { code });
        storeData("/player-url", digitalReceptionPublic.playerUrl);

        const hasTrailingSlash = digitalReceptionPublic.playerUrl.endsWith("/");

        window.location.replace(digitalReceptionPublic.playerUrl + (hasTrailingSlash ? "" : "/"));
      } catch (error) {
        setPinCode(defaultPinCode);
        setFocusedInput("1");
      }
    },
    {
      enabled: !!code && code.length === 6,
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnReconnect: true,
    },
  );

  refreshUntilMark("activation-refresh", 2000);

  return (
    <>
      <Typography variant="h4" className="font-weight-regular pin__title">
        {t("check:pinCode")}
      </Typography>
      <div>
        <PinInputs
          showValues={false}
          focusedInput={focusedInput}
          setFocusedInput={setFocusedInput}
          pinCode={pinCode}
          setPinCode={setPinCode}
          keyboard={keyboard}
        />
      </div>
      {(isVirtualKeyboardEnabled || !isPWA) && (
        <div
          ref={keyboardBox}
          className={clsx("keyboard ", { keyboard__out: !focusedInput })}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <VirtualKeyboard
            ref={keyboard}
            disableButtonHold
            layoutName="pin"
            inputName={focusedInput}
            onChange={onKeyboardChange}
            onKeyPress={onKeyboardKeyPress}
          />
        </div>
      )}
    </>
  );
};

export default Activation;
